import { supabase } from '@/lib/supabase';

export interface ChatProfile {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  role: string;
  email: string | null;
}

export interface Conversation {
  id: string;
  user_a: string;
  user_b: string;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  last_message_preview: string | null;
  other_user?: ChatProfile;
  unread_count?: number;
}

export interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  created_at: string;
  read_at: string | null;
}

export async function fetchConversations(userId: string): Promise<Conversation[]> {
  const { data, error } = await supabase
    .from('conversations')
    .select('*')
    .or(`user_a.eq.${userId},user_b.eq.${userId}`)
    .order('last_message_at', { ascending: false, nullsFirst: false });
  if (error) throw error;

  const conversations = (data ?? []) as Conversation[];
  if (conversations.length === 0) return [];

  const otherIds = conversations.map((c) => (c.user_a === userId ? c.user_b : c.user_a));
  const conversationIds = conversations.map((c) => c.id);

  const [profilesRes, unreadRes] = await Promise.all([
    supabase
      .from('profiles')
      .select('id, full_name, avatar_url, role, email')
      .in('id', otherIds),
    supabase
      .from('messages')
      .select('conversation_id')
      .in('conversation_id', conversationIds)
      .is('read_at', null)
      .neq('sender_id', userId),
  ]);

  const profileMap = new Map<string, ChatProfile>();
  for (const p of (profilesRes.data ?? []) as ChatProfile[]) {
    profileMap.set(p.id, p);
  }

  const unreadMap = new Map<string, number>();
  for (const m of (unreadRes.data ?? []) as { conversation_id: string }[]) {
    unreadMap.set(m.conversation_id, (unreadMap.get(m.conversation_id) ?? 0) + 1);
  }

  return conversations.map((c) => ({
    ...c,
    other_user: profileMap.get(c.user_a === userId ? c.user_b : c.user_a),
    unread_count: unreadMap.get(c.id) ?? 0,
  }));
}

export async function fetchMessages(conversationId: string): Promise<Message[]> {
  const { data, error } = await supabase
    .from('messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as Message[];
}

export async function sendMessage(
  conversationId: string,
  senderId: string,
  content: string,
): Promise<Message> {
  const { data, error } = await supabase
    .from('messages')
    .insert({ conversation_id: conversationId, sender_id: senderId, content })
    .select()
    .single();
  if (error) throw error;
  return data as Message;
}

export async function getOrCreateConversation(
  userA: string,
  userB: string,
): Promise<Conversation> {
  const a = userA < userB ? userA : userB;
  const b = userA < userB ? userB : userA;

  const { data: existing } = await supabase
    .from('conversations')
    .select('*')
    .eq('user_a', a)
    .eq('user_b', b)
    .maybeSingle();
  if (existing) return existing as Conversation;

  const { data, error } = await supabase
    .from('conversations')
    .insert({ user_a: a, user_b: b })
    .select()
    .single();
  if (error) throw error;
  return data as Conversation;
}

export async function searchProfiles(
  query: string,
  currentUserId: string,
): Promise<ChatProfile[]> {
  const trimmed = query.trim();
  if (trimmed.length < 2) return [];

  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name, avatar_url, role, email')
    .neq('id', currentUserId)
    .or(`full_name.ilike.%${trimmed}%,email.ilike.%${trimmed}%`)
    .limit(20);
  if (error) throw error;
  return (data ?? []) as ChatProfile[];
}

export async function markMessagesRead(
  conversationId: string,
  readerId: string,
): Promise<void> {
  await supabase
    .from('messages')
    .update({ read_at: new Date().toISOString() })
    .eq('conversation_id', conversationId)
    .neq('sender_id', readerId)
    .is('read_at', null);
}